import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { AlertContext } from "~/App";
import api from "~/services/api";
import { toast } from "react-toastify";
import CancelRecurrence from "./CancelRecurrence";
import ModalFilters from "./ModalFilters";

export default function FinancialRecurrence() {
  const filial = useSelector((state) => state.auth.filial);
  const defaultOrderBy = { column: "name", asc: true };
  const [status, setStatus] = useState("All");
  const [hasRecurrence, setHasRecurrence] = useState("All");
  const [isAutopay, setIsAutopay] = useState("All");
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const defaultGridHeader = [
    {
      title: "Registration Number",
      name: "registration_number",
      type: "text",
      filter: false,
    },
    {
      title: "Name",
      name: "name",
      type: "text",
      filter: false,
    },
    {
      title: "Last Name",
      name: "last_name",
      type: "text",
      filter: false,
    },
    {
      title: "Has Recurrence",
      name: "has_recurrence",
      type: "boolean",
      filter: false,
    },
    {
      title: "Status",
      name: "recurrence_status",
      type: "text",
      filter: false,
    },
  ];
  const [selected, setSelected] = useState([]);
  const [cancelRecurrenceOpen, setCancelRecurrenceOpen] = useState(false);
  const { alertBox } = useContext(AlertContext);

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "financial-recurrence"
  );

  useEffect(() => {
    setSearch("");
    setPage(1);
  }, []);

  async function loader() {
    setLoadingData(true);

    if (status === "Global") {
      try {
        const orderFix = orderBy?.column ?? "name";
        const orderDir = orderBy ? (orderBy.asc ? "ASC" : "DESC") : "ASC";
        const { data } = await api.get(
          `/contractsessions?limit=${limit}&page=${page}&orderBy=${orderFix}&orderASC=${orderDir}&search=${search ? search.value : ""}`
        );
        const rows = data.rows ?? [];
        const totalRows = data.totalRows ?? rows.length;
        const pages = Math.ceil(totalRows / limit) || 1;
        setPages(pages);
        setGridDetails({ totalRows, pages });
        setTotalRows(totalRows);

        const gridDataValues = rows.map((session, index) => ({
          show: true,
          id: session.id,
          isNewRecurrence: false,
          isGlobalSession: true,
          fields: [
            session.registration_number ?? "-",
            session.name ?? "-",
            session.last_name ?? "-",
            true,
            "Global",
          ],
          selectable: true,
          canceled: null,
          page: Math.ceil((index + 1) / limit),
        }));

        setGridData(gridDataValues);
      } catch (err) {
        console.log(err);
      }
      setLoadingData(false);
      return;
    }

    const data = await getData("recurrence", {
      limit,
      page,
      orderBy,
      setPages,
      setGridData,
      search,
      defaultGridHeader,
      defaultOrderBy,
      setGridDetails,
      extraParams: { status, has_recurrence: hasRecurrence, is_autopay: isAutopay },
    });
    if (!data) {
      setLoadingData(false);
      return;
    }
    const gridDataValues = data.flatMap(
      ({ registration_number, id, name, last_name, issuer, canceled_at }) => {
        if (!issuer) {
          return [{
            show: true,
            id,
            isNewRecurrence: true,
            fields: [registration_number, name, last_name, false, "Draft"],
            selectable: true,
            canceled: canceled_at,
          }];
        }

        const recurrenceList = Array.isArray(issuer?.issuer_x_recurrence)
          ? issuer.issuer_x_recurrence
          : issuer?.issuer_x_recurrence
          ? [issuer.issuer_x_recurrence]
          : [];

        if (recurrenceList.length === 0) {
          return [{
            show: true,
            id,
            isNewRecurrence: true,
            fields: [registration_number, name, last_name, false, "Draft"],
            selectable: true,
            canceled: canceled_at,
          }];
        }

        return recurrenceList.flatMap((r) => {
          const baseRow = {
            show: true,
            id: r.id,
            student_id: id,
            isNewRecurrence: false,
            fields: [registration_number, name, last_name, r.active, r.recurrence_status ?? null],
            selectable: r.active === true,
            canceled: canceled_at,
          };

          if (r.recurrence_status === 'Ex-student') {
            const draftRow = {
              show: true,
              id,
              student_id: id,
              isNewRecurrence: true,
              fields: [registration_number, name, last_name, false, 'Draft'],
              selectable: false,
              canceled: null,
            };
            return [baseRow, draftRow];
          }

          return [baseRow];
        });
      }
    ).map((row, index) => ({ ...row, page: Math.ceil((index + 1) / limit) }));
    setGridData(gridDataValues);
    setLoadingData(false);
  }

  useEffect(() => {
    setStatus("All");
  }, [search?.value]);

  useEffect(() => {
    if (!opened) {
      loader();
    }
  }, [opened, filial, orderBy, search, limit, page, status, hasRecurrence, isAutopay]);

  function handleCancelRecurrence() {
    const newVarOpened = !cancelRecurrenceOpen;
    setCancelRecurrenceOpen(newVarOpened);
    if (!newVarOpened) {
      setSelected([]);
      loader();
    }
    handleOpened(null);
  }

  function handleStopRecurrence() {
    if (selected.length === 0) {
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to stop this recurrence? This action will remove all pending receivables and cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            for (let student of selected) {
              try {
                await api
                  .delete(`/recurrence/${student.id}`)
                  .then((response) => {
                    console.log(response);
                  })
                  .catch((err) => {
                    console.log(err);
                  });
              } catch (err) {
                console.log(err);
              }
            }
            toast("Recurrence stopped!", { autoClose: 1000 });
            setSelected([]);
            loader();
          },
        },
      ],
    });
  }

  const selectionFunctions = [];

  if (selected.length > 0) {
    selectionFunctions.push({
      title: "Cancel Recurrence",
      alias: "cancel-recurrence",
      fun: handleCancelRecurrence,
      icon: "XCircle",
      Page: CancelRecurrence,
      opened: cancelRecurrenceOpen,
      setOpened: setCancelRecurrenceOpen,
      selected,
    });
  }

  function handleApplyFilters({ status: s, hasRecurrence: hr, isAutopay: autopay }) {
    setStatus(s);
    setHasRecurrence(hr);
    setIsAutopay(autopay);
    setPage(1);
  }

  return (
    <>
      <ModalFilters
        opened={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onApplyFilters={handleApplyFilters}
      />
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        handleNew={false}
        defaultGridHeader={defaultGridHeader}
        filtersModal={{ onOpen: () => setFiltersModalOpen(true) }}
        selection={{
          multiple: false,
          selected,
          setSelected,
          functions: selectionFunctions,
        }}
      />
    </>
  );
}
